/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const Home = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('animate-fade-in-up')),
      { threshold: 0.05 }
    );
    document.querySelectorAll('.scroll-animate').forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const handleSubmit = (e) => {
    e.preventDefault();
    alert('Message envoyé !');
    setForm({ name: '', email: '', phone: '', subject: '', message: '' });
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white overflow-x-hidden">

      {/* ══════════════════════════════════════════════════════════ */}
      {/* 1. NAVBAR — Logo unique                                    */}
      {/* ══════════════════════════════════════════════════════════ */}
      <nav className="bg-[#0A0A0A] border-b border-gray-900 sticky top-0 z-50">
        <div className="w-full px-8 lg:px-16 py-4 flex items-center justify-between">

          <Link to="/" className="flex items-center">
            <img
              src="/images/logo.png"
              alt="SamaEcole"
              className="h-14 w-auto object-contain"
            />
          </Link>

          <div className="hidden md:flex items-center gap-10">
            <a href="#" className="text-[#F39C12] font-medium text-sm border-b-2 border-[#F39C12] pb-1">Accueil</a>
            <a href="#fonctionnalites" className="text-gray-300 hover:text-[#F39C12] transition text-sm">Fonctionnalités</a>
            <a href="#apropos" className="text-gray-300 hover:text-[#F39C12] transition text-sm">À propos</a>
            <a href="#contact" className="text-gray-300 hover:text-[#F39C12] transition text-sm">Contact</a>
          </div>

          <Link
            to="/login"
            className="btn-orange flex items-center gap-2 text-white font-semibold text-sm px-5 py-2.5 rounded-full"
          >
            <LockIcon className="w-4 h-4" />
            <span>Se connecter</span>
          </Link>
        </div>
      </nav>

      {/* ══════════════════════════════════════════════════════════ */}
      {/* 2. HERO — fond noir + arc orange en haut à droite          */}
      {/* ══════════════════════════════════════════════════════════ */}
      <section className="relative bg-[#0A0A0A] overflow-hidden">

        {/* Arc orange géant (haut droite) */}
        <div className="absolute top-0 right-0 w-[55%] h-full pointer-events-none hidden lg:block">
          <svg viewBox="0 0 800 600" className="w-full h-full" preserveAspectRatio="xMidYMid slice">
            <path
              d="M 800 0 L 800 600 L 350 600 Q 500 300 450 0 Z"
              fill="url(#heroGradient)"
              opacity="0.95"
            />
            <defs>
              <linearGradient id="heroGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#F39C12" />
                <stop offset="100%" stopColor="#D35400" />
              </linearGradient>
            </defs>
          </svg>
        </div>

        <div className="relative w-full px-8 lg:px-16 py-12 grid lg:grid-cols-2 gap-8 items-center">

          {/* Texte gauche */}
          <div className="relative z-10">
            <h1 className="text-[42px] md:text-[54px] font-extrabold leading-[1.05] tracking-tight">
              Une gestion scolaire
              <br />
              <span className="text-[#F39C12]">simple et efficace</span>
            </h1>

            <p className="text-gray-300 mt-6 text-sm leading-relaxed max-w-md">
              SamaEcole est une plateforme complète qui facilite la gestion
              de votre établissement scolaire. Suivez les notes, les absences,
              les paiements et bien plus encore, en toute sécurité.
            </p>

            <div className="flex flex-wrap gap-3 mt-8">
              <Link
                to="/login"
                className="btn-orange flex items-center gap-2 text-white font-semibold text-sm px-6 py-3 rounded-full"
              >
                <LockIcon className="w-4 h-4" />
                <span>Se connecter</span>
              </Link>

              <a
                href="#fonctionnalites"
                className="btn-outline-orange flex items-center gap-2 text-white font-semibold text-sm px-6 py-3 rounded-full"
              >
                <PlayIcon className="w-3.5 h-3.5" />
                <span>Découvrir la plateforme</span>
              </a>
            </div>
          </div>

          {/* Image droite */}
          <div className="relative z-10 flex justify-end">
            <div className="relative w-full max-w-[600px]">
              <img
                src="/images/hero-students.png"
                alt="Étudiants SamaEcole"
                className="w-full h-auto object-contain"
              />
              <div className="absolute bottom-3 right-4 text-right">
                <div className="text-[#F39C12] italic font-semibold text-lg leading-tight drop-shadow-[0_2px_4px_rgba(0,0,0,0.9)]">
                  Apprendre<br />aujourd'hui,<br />réussir demain
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════ */}
      {/* 3. 4 CARTES RÔLES                                          */}
      {/* ══════════════════════════════════════════════════════════ */}
      <section id="fonctionnalites" className="bg-[#0A0A0A] pb-12">
        <div className="w-full px-8 lg:px-16">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <RoleCard icon={<UserIcon className="w-5 h-5 text-white" />} title="Administrateur" description="Gérez tout votre établissement en toute simplicité." delay="delay-100" />
            <RoleCard icon={<TeacherIcon className="w-5 h-5 text-white" />} title="Professeur" description="Saisissez les notes, suivez vos élèves et partagez vos appréciations." delay="delay-200" />
            <RoleCard icon={<CapIcon className="w-5 h-5 text-white" />} title="Élève" description="Consultez vos notes, votre emploi du temps et vos bulletins." delay="delay-300" />
            <RoleCard icon={<FamilyIcon className="w-5 h-5 text-white" />} title="Parent" description="Suivez la scolarité de votre enfant à tout moment." delay="delay-400" />
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════ */}
      {/* 4. BANDEAU ORANGE STATS                                    */}
      {/* ══════════════════════════════════════════════════════════ */}
      <section className="relative py-16 overflow-hidden bg-gradient-to-r from-[#E67E22] via-[#F39C12] to-[#E67E22]">

        {/* Arc noir haut gauche */}
        <svg className="absolute top-0 left-0 w-[40%] h-[70%] pointer-events-none" viewBox="0 0 400 300" preserveAspectRatio="none">
          <path d="M 0 0 L 0 300 Q 200 150 400 0 Z" fill="#0A0A0A" />
        </svg>

        {/* Arc noir bas droite */}
        <svg className="absolute bottom-0 right-0 w-[40%] h-[70%] pointer-events-none" viewBox="0 0 400 300" preserveAspectRatio="none">
          <path d="M 400 300 L 400 0 Q 200 150 0 300 Z" fill="#0A0A0A" />
        </svg>

        <div className="relative w-full px-8 lg:px-16 grid lg:grid-cols-5 gap-10 items-center">

          <div className="lg:col-span-2">
            <div className="w-10 h-0.5 bg-black mb-3"></div>
            <h2 className="text-[#E67E22] font-bold text-base mb-2">Une école plus connectée</h2>
            <p className="text-white font-extrabold text-2xl leading-tight">
              Des résultats concrets<br />
              pour une meilleure éducation
            </p>
          </div>

          <div className="lg:col-span-3 grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCircle icon={<CapIcon className="w-5 h-5 text-[#F39C12]" />} value="100%" label="Numérique" description="Une plateforme 100% en ligne" />
            <StatCircle icon={<ClockIcon className="w-5 h-5 text-[#F39C12]" />} value="24/7" label="Accès" description="À la plateforme à tout moment" />
            <StatCircle icon={<UsersIcon className="w-5 h-5 text-[#F39C12]" />} value="4" label="Espaces utilisateurs" description="Administrateur, Professeur, Élève, Parent" />
            <StatCircle icon={<ShieldIcon className="w-5 h-5 text-[#F39C12]" />} value="100%" label="Sécurité" description="Vos données sont protégées" />
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════ */}
      {/* 5. SECTION BLANCHE                                         */}
      {/* ══════════════════════════════════════════════════════════ */}
      <section id="apropos" className="relative bg-white text-gray-900 py-16 overflow-hidden">

        {/* Cercle orange géant à droite */}
        <div className="absolute top-0 -right-24 w-[480px] h-[480px] rounded-full bg-gradient-to-br from-[#F39C12] to-[#E67E22] hidden lg:block"></div>

        <div className="relative w-full px-8 lg:px-16 grid lg:grid-cols-2 gap-10 items-center">

          <div className="scroll-animate z-10">
            <div className="w-12 h-1 bg-[#F39C12] mb-4"></div>
            <h2 className="text-[32px] md:text-[40px] font-extrabold leading-[1.1] tracking-tight">
              <span className="text-gray-900">Sama</span>
              <span className="text-[#F39C12]">Ecole</span>
              <br />
              <span className="text-gray-900">Plus qu'un logiciel,</span>
              <br />
              <span className="text-[#F39C12]">une solution pour l'éducation</span>
            </h2>

            <p className="text-gray-600 mt-5 text-sm leading-relaxed max-w-md">
              Nous croyons en une école connectée, moderne et accessible
              à tous. SamaEcole vous accompagne dans la réussite scolaire
              de vos enfants.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 mt-8">
              <SmallFeature icon={<ClockIcon className="w-4 h-4 text-white" />} title="Gain de temps" description="Une gestion centralisée et simplifiée." />
              <SmallFeature icon={<LinkIcon className="w-4 h-4 text-white" />} title="Accessibilité" description="Partout, sur tous vos appareils." />
              <SmallFeature icon={<RefreshIcon className="w-4 h-4 text-white" />} title="Suivi en temps réel" description="Des informations toujours à jour." />
              <SmallFeature icon={<ShieldIcon className="w-4 h-4 text-white" />} title="Fiabilité" description="Des données sécurisées et fiables." />
              <SmallFeature icon={<ChatIcon className="w-4 h-4 text-white" />} title="Communication facilitée" description="Entre tous les acteurs de l'école." />
              <SmallFeature icon={<ChartIcon className="w-4 h-4 text-white" />} title="Performance" description="Une solution évolutive et innovante." />
            </div>
          </div>

          <div className="scroll-animate relative z-10 flex justify-center">
            <img
              src="/images/about-desk.png"
              alt="Bureau avec livres et ordinateur"
              className="w-full max-w-[520px] h-auto object-contain"
            />
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════ */}
      {/* 6. CONTACT                                                 */}
      {/* ══════════════════════════════════════════════════════════ */}
      <section id="contact" className="relative bg-[#0A0A0A] py-16 overflow-hidden">
        <div className="w-full px-8 lg:px-16 grid lg:grid-cols-2 gap-12 items-start">

          <div className="scroll-animate">
            <div className="w-12 h-1 bg-[#F39C12] mb-4"></div>
            <h2 className="text-[#F39C12] font-bold text-base mb-2">Contactez-nous</h2>
            <p className="text-white font-extrabold text-3xl leading-tight mb-6">
              Une question ?<br />
              Notre équipe est à votre écoute
            </p>

            <p className="text-gray-400 text-sm leading-relaxed max-w-md mb-8">
              Vous avez une question, une demande d'information ou besoin
              d'assistance ? N'hésitez pas à nous contacter, nous vous
              répondons rapidement.
            </p>

            <div className="space-y-4">
              <ContactItem icon={<MailIcon className="w-4 h-4 text-white" />} text="tineaida91@gmail.com" />
              <ContactItem icon={<PhoneIcon className="w-4 h-4 text-white" />} text="+221 78 366 61 98" />
              <ContactItem icon={<PinIcon className="w-4 h-4 text-white" />} text="Dakar, Sénégal" />
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            className="scroll-animate bg-[#0F0F0F] border border-gray-800 rounded-2xl p-6 space-y-3"
          >
            <div className="grid md:grid-cols-2 gap-3">
              <InputField icon={<UserIcon className="w-4 h-4" />} name="name" placeholder="Nom complet" value={form.name} onChange={handleChange} />
              <InputField icon={<MailIcon className="w-4 h-4" />} name="email" type="email" placeholder="E-mail" value={form.email} onChange={handleChange} />
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              <InputField icon={<PhoneIcon className="w-4 h-4" />} name="phone" placeholder="Téléphone" value={form.phone} onChange={handleChange} />
              <InputField icon={<PenIcon className="w-4 h-4" />} name="subject" placeholder="Objet" value={form.subject} onChange={handleChange} />
            </div>

            <div className="relative">
              <textarea
                name="message"
                rows="5"
                placeholder="Votre message"
                value={form.message}
                onChange={handleChange}
                className="w-full bg-[#0A0A0A] border border-gray-800 rounded-lg pt-3 pl-10 pr-4 pb-3 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-[#F39C12] transition resize-none"
              />
              <span className="absolute top-3 left-3 text-gray-500">
                <ChatIcon className="w-4 h-4" />
              </span>
            </div>

            <button
              type="submit"
              className="btn-orange w-full text-white font-semibold text-sm py-3 rounded-full flex items-center justify-center gap-2"
            >
              <SendIcon className="w-4 h-4" />
              <span>Envoyer le message</span>
            </button>
          </form>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════ */}
      {/* 7. FOOTER — Logo unique                                    */}
      {/* ══════════════════════════════════════════════════════════ */}
      <footer className="bg-[#0A0A0A] border-t border-gray-900 pt-12 pb-5">
        <div className="w-full px-8 lg:px-16 grid grid-cols-2 md:grid-cols-5 gap-8">

          <div className="col-span-2 md:col-span-1">
            <img
              src="/images/logo.png"
              alt="SamaEcole"
              className="h-12 w-auto object-contain mb-2"
            />
          </div>

          <div>
            <h4 className="text-[#F39C12] font-bold text-sm mb-3">Navigation</h4>
            <ul className="space-y-1.5 text-xs text-gray-400">
              <li><a href="#" className="hover:text-white transition">Accueil</a></li>
              <li><a href="#fonctionnalites" className="hover:text-white transition">Fonctionnalités</a></li>
              <li><a href="#apropos" className="hover:text-white transition">À propos</a></li>
              <li><a href="#contact" className="hover:text-white transition">Contact</a></li>
            </ul>
          </div>

          <div>
            <h4 className="text-[#F39C12] font-bold text-sm mb-3">Utilisateurs</h4>
            <ul className="space-y-1.5 text-xs text-gray-400">
              <li>Administrateur</li>
              <li>Professeur</li>
              <li>Élève</li>
              <li>Parent</li>
            </ul>
          </div>

          <div>
            <h4 className="text-[#F39C12] font-bold text-sm mb-3">Sécurité</h4>
            <div className="flex gap-2 text-[11px] text-gray-400">
              <ShieldIcon className="w-5 h-5 text-[#F39C12] flex-shrink-0" />
              <p>Vos données sont protégées et accessibles uniquement aux personnes autorisées.</p>
            </div>
          </div>

          <div>
            <h4 className="text-[#F39C12] font-bold text-sm mb-3">Suivez-nous</h4>
            <div className="flex gap-2 flex-wrap">
              <SocialIcon><FacebookIcon /></SocialIcon>
              <SocialIcon><TwitterIcon /></SocialIcon>
              <SocialIcon><InstagramIcon /></SocialIcon>
              <SocialIcon><YoutubeIcon /></SocialIcon>
              <SocialIcon><LinkedinIcon /></SocialIcon>
            </div>
          </div>
        </div>

        <div className="w-full px-8 lg:px-16 mt-10 pt-5 border-t border-gray-900 flex flex-col md:flex-row justify-between gap-2 text-[11px] text-gray-500">
          <p>© {new Date().getFullYear()} SamaEcole. Tous droits réservés.</p>
          <div className="flex gap-3">
            <a href="#" className="hover:text-white transition">Politique de confidentialité</a>
            <span>|</span>
            <a href="#" className="hover:text-white transition">Conditions d'utilisation</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// ═══════════════ COMPOSANTS UTILITAIRES ══════════════════════════
// ═══════════════════════════════════════════════════════════════════

function RoleCard({ icon, title, description, delay = '' }) {
  return (
    <div className={`scroll-animate ${delay} card-hover bg-[#0F0F0F] border border-gray-800 rounded-xl p-5 flex flex-col`}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-full bg-[#F39C12] flex items-center justify-center flex-shrink-0">
          {icon}
        </div>
        <h3 className="text-base font-bold text-white">{title}</h3>
      </div>
      <p className="text-gray-400 text-xs leading-relaxed mb-3 flex-1">{description}</p>
      <div className="text-[#F39C12] text-base">→</div>
    </div>
  );
}

function StatCircle({ icon, value, label, description }) {
  return (
    <div className="text-center">
      <div className="relative w-24 h-24 mx-auto rounded-full bg-black border-2 border-[#F39C12]/40 flex items-center justify-center mb-3 shadow-lg shadow-black/50">
        <div className="absolute -top-1 -right-1 w-7 h-7 rounded-full bg-black border border-[#F39C12]/40 flex items-center justify-center">
          {icon}
        </div>
        <span className="text-white font-extrabold text-xl">{value}</span>
      </div>
      <h3 className="text-white font-bold text-sm mb-0.5">{label}</h3>
      <p className="text-white/70 text-[10px] leading-snug">{description}</p>
    </div>
  );
}

function SmallFeature({ icon, title, description }) {
  return (
    <div className="flex gap-3 items-start">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#F39C12] text-white flex items-center justify-center shadow-md">
        {icon}
      </div>
      <div>
        <h4 className="font-bold text-gray-900 text-xs">{title}</h4>
        <p className="text-gray-500 text-[11px] leading-snug mt-0.5">{description}</p>
      </div>
    </div>
  );
}

function ContactItem({ icon, text }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 rounded-full bg-[#F39C12] flex items-center justify-center text-white">
        {icon}
      </div>
      <span className="text-gray-300 text-sm">{text}</span>
    </div>
  );
}

function InputField({ icon, name, type = 'text', placeholder, value, onChange }) {
  return (
    <div className="relative">
      <input
        type={type}
        name={name}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        className="w-full bg-[#0A0A0A] border border-gray-800 rounded-lg pl-10 pr-4 py-2.5 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-[#F39C12] transition"
      />
      <span className="absolute top-1/2 -translate-y-1/2 left-3 text-gray-500">{icon}</span>
    </div>
  );
}

function SocialIcon({ children }) {
  return (
    <a href="#" className="w-8 h-8 rounded-full bg-[#F39C12] hover:bg-[#E67E22] flex items-center justify-center text-white transition-all duration-300 hover:scale-110">
      {children}
    </a>
  );
}

// ═══════════════ ICÔNES SVG ═══════════════════════════════════════
const LockIcon = ({ className }) => (<svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>);
const PlayIcon = ({ className }) => (<svg className={className} fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>);
const UserIcon = ({ className }) => (<svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="8" r="4" /><path d="M4 21v-2a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v2" /></svg>);
const UsersIcon = ({ className }) => (<svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="9" cy="8" r="4" /><path d="M2 21v-2a4 4 0 0 1 4-4h6a4 4 0 0 1 4 4v2" /><path d="M16 4a4 4 0 0 1 0 8" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /></svg>);
const TeacherIcon = ({ className }) => (<svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M22 10v6M2 10l10-5 10 5-10 5z" /><path d="M6 12v5c3 3 9 3 12 0v-5" /></svg>);
const FamilyIcon = ({ className }) => (<svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="8" cy="8" r="3" /><circle cx="17" cy="9" r="2.5" /><path d="M2 20v-1a5 5 0 0 1 5-5h2a5 5 0 0 1 5 5v1" /><path d="M15 20v-1a4 4 0 0 1 4-4" /></svg>);
const CapIcon = ({ className }) => (<svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M22 10L12 5 2 10l10 5 10-5z" /><path d="M6 12v5c0 1 2 3 6 3s6-2 6-3v-5" /></svg>);
const ClockIcon = ({ className }) => (<svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>);
const ShieldIcon = ({ className }) => (<svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>);
const LinkIcon = ({ className }) => (<svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>);
const RefreshIcon = ({ className }) => (<svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M23 4v6h-6M1 20v-6h6" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></svg>);
const ChatIcon = ({ className }) => (<svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>);
const ChartIcon = ({ className }) => (<svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M3 3v18h18" /><path d="M7 12l4 4 8-8" /></svg>);
const MailIcon = ({ className }) => (<svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="M22 6l-10 7L2 6" /></svg>);
const PhoneIcon = ({ className }) => (<svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13 1 .37 2 .7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.33 1.87.57 2.81.7A2 2 0 0 1 22 16.92z" /></svg>);
const PinIcon = ({ className }) => (<svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>);
const PenIcon = ({ className }) => (<svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" /></svg>);
const SendIcon = ({ className }) => (<svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" /></svg>);
const FacebookIcon = () => (<svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M22 12a10 10 0 1 0-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.5 1.49-3.89 3.77-3.89 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.77l-.44 2.89h-2.33v6.99A10 10 0 0 0 22 12z" /></svg>);
const TwitterIcon = () => (<svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>);
const InstagramIcon = () => (<svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="2" y="2" width="20" height="20" rx="5" /><circle cx="12" cy="12" r="4" /><circle cx="17.5" cy="6.5" r="1" fill="currentColor" /></svg>);
const YoutubeIcon = () => (<svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M23 12s0-3.4-.43-5.03a2.6 2.6 0 0 0-1.83-1.84C19.1 4.7 12 4.7 12 4.7s-7.1 0-8.74.43A2.6 2.6 0 0 0 1.43 6.97C1 8.6 1 12 1 12s0 3.4.43 5.03a2.6 2.6 0 0 0 1.83 1.84c1.64.43 8.74.43 8.74.43s7.1 0 8.74-.43a2.6 2.6 0 0 0 1.83-1.84C23 15.4 23 12 23 12zM9.75 15.5v-7l6 3.5-6 3.5z" /></svg>);
const LinkedinIcon = () => (<svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.13 1.45-2.13 2.94v5.67H9.35V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.12 20.45H3.55V9h3.57v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z" /></svg>);

export default Home;